import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

i18n
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    debug: process.env.I18N_DEBUG === "true",
    fallbackLng: "en",
    locales: [
      "en",
      "ar-SA",
      "ca-ES",
      "cs-CZ",
      "me-ME",
      "cy-GB",
      "da-DK",
      "de-DE",
      "el-GR",
      "et-EE",
      "es-ES",
      "es-LA",
      "fr-FR",
      "he-IL",
      "hi-IN",
      "hr-HR",
      "it-IT",
      "ja-JP",
      "kn-IN",
      "ko-KR",
      "mr-IN",
      "hu-HU",
      "nl-NL",
      "no-NO",
      "pl-PL",
      "pt-BR",
      "pt-PT",
      "ro-RO",
      "ru-RU",
      "sk-SK",
      "sl-SI",
      "fi-FI",
      "sv-SE",
      "vls-BE",
      "sr-SP",
      "tr-TR",
      "uk-UA",
      "zh-CN",
      "zh-TW",
      "xx-XX",
    ],
    // currently supportedLngs is the same as locales but clever lang loading
    // could reduce the number of supported translation files e.g. 'fr' over 'fr-FR' + variants
    supportedLngs: [
      "en",
      "ar-SA",
      "ca-ES",
      "cs-CZ",
      "me-ME",
      "cy-GB",
      "da-DK",
      "de-DE",
      "el-GR",
      "et-EE",
      "es-ES",
      "es-LA",
      "fr-FR",
      "he-IL",
      "hi-IN",
      "hr-HR",
      "it-IT",
      "ja-JP",
      "kn-IN",
      "ko-KR",
      "mr-IN",
      "hu-HU",
      "nl-NL",
      "no-NO",
      "pl-PL",
      "pt-BR",
      "pt-PT",
      "ro-RO",
      "ru-RU",
      "sk-SK",
      "sl-SI",
      "fi-FI",
      "sv-SE",
      "vls-BE",
      "sr-SP",
      "tr-TR",
      "uk-UA",
      "zh-CN",
      "zh-TW",
      "xx-XX",
    ],
    load: "currentOnly", // otherwise for fr-FR it's load ['fr-FR', 'fr']

    // nonExplicitSupportedLngs: true, // allows locale variants on supportedLngs
    detection: {
      order: ["path"], // only use path to detect local for now
    },

    interpolation: {
      escapeValue: false, // not needed for react!!
    },
    resources: {
      en: {
        translation: {
          modal: {
            close: "Close",
            error: {
              heading: "An error has occurred",
              externalLink: {
                message:
                  "Unfortunately links to external sites are not available in the Editor.",
              },
            },
          },
          betaBanner: {
            buttonLabel: "close",
            buttonTitle: "close",
            message: "The Code Editor is in",
            feedbackText: "Give us your",
            feedback: "feedback",
            feedbackImprove: "to help us improve.",
            modal: {
              close: "Close",
              heading: "Code Editor is in beta",
              meaningHeading: "What does beta mean?",
              meaningText:
                "Beta means that we are not quite finished yet, and some things might not look or work as well as we’d like. However, it also means you are one of the first people to use our new Code Editor!",
            },
            modalLink: "beta",
            beta: "Beta",
          },
          betaFeedback: {
            title: "Feedback & updates",
            beta: "beta",
            text: "Code Editor for Education is currently in <0>beta</0>. Complete this <1>survey</1> to share your feedback.",
            productUpdates: {
              date: "🚀 Latest updates",
              list: [
                "View students who have finished their projects",
                "Add additional teachers to your class",
                "Share direct links to projects and classes with students",
              ],
              link: "View full updates",
            },
          },
          classes: {
            teacherTitle: "Classes",
            teacherDescription:
              "Classes are used to group students and assign projects to them.",
            studentTitle: "Your classes",
            studentDescription:
              "Here are all the classes you have been added to.",
            createClass: "Create class",
            noClasses: "You don't have any classes at the moment",
          },
          classMembers: {
            noMembers: "This class doesn't have any members yet",
          },
          classModal: {
            create: {
              title: "Create a new class",
              confirm: "Create class",
              cancel: "Cancel",
            },
            update: {
              title: "Edit class details",
              confirm: "Update class details",
              cancel: "Cancel",
            },
            description:
              "Classes are used to group students and assign projects to them. We recommend using a consistent naming convention to help you to identify your classes easily.",
            className: "Class name",
            classNameHint: "Your class name will be visible to your students",
            classDescription: "Class description (optional)",
            classDescriptionHint:
              "Your class description will be visible to your students",
            nameMissing: "You must supply a name",
          },
          editorHome: {
            text: "Code Editor home",
          },
          editorPanel: {
            ariaLabel: "editor text input",
          },
          filePanel: {
            errors: {
              containsSpaces: "File names must not contain spaces.",
              generalError: "Error",
              notUnique: "File names must be unique.",
              or: "or",
              unsupportedExtension:
                "File names must end in {{allowedExtensions}}.",
            },
            files: "Project files",
            images: "Image gallery",
            newFileButton: "Add file",
            newFileModal: {
              cancel: "Cancel",
              heading: "Add a new file to your project",
              helpText:
                "Remember to add the file extension at the end of your file name, for example, {{examples}}",
              helpTextExample: {
                html: "'file.html', 'file.css' or 'file.js'",
                python: "'file.py'",
              },
              inputLabel: "Name your file",
              addFile: "Add file",
            },
            renameFileModal: {
              cancel: "Cancel",
              heading: "Rename file",
              inputLabel: "Name your file",
              save: "Save",
            },
            fileMenu: {
              label: "Open file menu",
              renameItem: "Rename file",
            },
          },
          downloadPanel: {
            heading: "Save & download",
            logInTitle: "Log in to save your progress",
            logInHint:
              "With a Raspberry Pi Account you can save your code and project steps progress.",
            logInButton: "Log in to save",
            signUpButton: "Sign up",
            downloadHint:
              "Download your project files so you can use them offline and in a different code editor.",
            downloadButton: "Download project",
          },
          landingPage: {
            title: "Code Editor",
            subtitle: "Start coding, no setup required!",
            chooseLanguage: "What do you want to code with?",
            python: "Python",
            html: "HTML/CSS",
            login: "Log in",
            whereToStart: "Not sure where to start?",
            projectPython: "Python path",
            projectHtml: "Web path",
            loginMessage:
              "Have an account?<loginButton/>and continue your projects",
            followAPath:
              "Follow a <pythonLink>{{python}}</pythonLink> or <htmlLink>{{html}}</htmlLink> on our Projects site.",
          },
          educationLandingPage: {
            hero: {
              title: "Same great code editor, now in your classroom.",
              subtitle: "Free now, free forever.",
              imageAlt: "A young girl using the Code editor in a classroom.",
              createSchool: "Create your school account",
              logIn: "Log in as a student",
              schoolDashboard: "View your school",
            },
            beta: {
              title: "Now in beta!",
              text: "Code Editor For Education is currently in beta. This means you get to use the platform sooner, but not all features are complete. If you’d like to know more, see our <0>help articles</0>.",
            },
            getStarted: {
              title: "Register your school to get started!",
              button: "Create your school account",
            },
            ide: {
              title: "An IDE designed for learners",
              text: "Tailored specifically to young people's needs, our integrated development environment (IDE) helps make learning text-based programming simple and accessible for children aged 9 and up. It’s safe, age-appropriate, and suitable for use in the classroom.",
              imageAlt:
                "A screenshot of the Code Editor showing a Python project that generates geometric patterns from a string. Alongside the code there are some instructions for the student",
            },
            free: {
              title: "All features, totally free",
              text: "Access the Code Editor in your web browser without installing any additional software. Add an unlimited number of projects, lessons, teachers, and students to your school account, all for free.",
            },
            engage: {
              title: "Create engaging coding lessons",
              text: "Create custom coding projects for your students to work on. Help them code their own games and art using Python, or design websites in HTML/CSS/JavaScript. Your students can get creative with code with our wide choice of Python libraries!\n\nYou can also give your students guidance and support by adding custom instructions to the projects you share with them.",
              imageAlt:
                "A collage of images showing different visual outputs from the Code Editor. Such as, geometric patterns, a solar system simulation and a map visualisation.",
            },
            feedback: {
              title: "Give students personalised feedback",
              text: "Easily check your students’ progress, view their coding projects, and share individual, instant feedback with each student to keep them on track.",
              imageAlt:
                "A screenshot from the Code Editor which shows a teacher’s comment on a student's code. The Teacher's comment reads “Looks great! Just one small mistake to fix”. Referring to a typo in the student's code.",
              tag: "Future feature",
            },
            class: {
              title: "Simple and easy class management",
              text: "Like the Editor itself, we’ve kept our educator interface clean, simple, and easy to use following feedback from teachers. Create and manage student accounts easily. You can add students in bulk through a .csv file, organise your students into classes and help them reset their passwords quickly.",
            },
            safe: {
              title: "Safe and private by design",
              text: "Accounts for education are designed to be safe for students of all ages. We take safeguarding seriously, with one-way feedback to students, visibility of their work at all times, and verified school accounts.\n\nIn line with best practice codes protecting children online, we minimise our data capture so that we have just enough to keep students safe.",
            },
            people: {
              title: "What do people say about our Editor?",
              text: "<i>“We have used it and love it, the fact that it is both for HTML / CSS and then Python is great as the students have a one-stop shop for IDEs.”</i><br/><strong>Lee Willis, Head of ICT and Computing, Newcastle High School for Girls</strong><br/><br/><i>“The class management and feedback features they're working on at the moment look really promising.”</i><br/><strong>Pete Dring, Head of Computing, Fulford School</strong>",
              imageAlt: "Students working on computers in a classroom.",
            },
          },
          educationLogo: {
            codeEditor: "Code Editor",
            forEducation: "For Education",
          },
          multiStepForm: {
            cancel: "Cancel",
            continue: "Continue",
            back: "Back",
            submit: "Submit",
            errorTitle: "There is a problem with the form",
            stepText: "Step {{current}} of {{total}}",
          },
          invitation: {
            confirmation: {
              title: "You joined {{schoolName}}!",
              button: "Go to School Dashboard",
            },
            invalidToken: {
              title: "You cannot accept this invite with this account",
              explanationOne:
                "It looks like you've been invited to join a school, but you're logged in with a different Raspberry Pi account. To accept the invitation, please log in or create an account using the email address to which the invitation was sent, and then try opening the invitation link again.",
              explanationTwo:
                "If you continue to have issues, please contact your school owner for assistance.",
              cancel: "Cancel",
            },
            start: {
              subtitle: "You have been invited to join:",
              continue: "Continue",
            },
            responsibilities: {
              title: "Do you accept these responsibilities?",
              listIntro: "As a <strong>teacher</strong> you must agree to:",
              listItemOne:
                "Be responsible for how students use the platform within your school account.",
              listItemTwo:
                "Make sure that features are used in line with your school’s internal policies and practices.",
              fullResponsibilities:
                "Your full responsibilities are outlined in the school account <0>Terms and Conditions</0>, so please ensure you read these.",
              acceptTerms:
                "I accept the responsibilities and the Terms and Conditions.",
              changeEmail:
                "I understand I will need to contact the Raspberry Pi Foundation to change my email address or delete my Raspberry Pi Foundation account while I am part of this school account.",
              back: "Back",
              joinSchool: "Join school",
              errorTitle: "There is a problem with the form",
              errorText:
                "You must agree to the responsibilities and Terms and Conditions to continue.",
            },
          },
          schoolOnboarding: {
            step: "Step title",
            steps: {
              errors: {
                401: "You must be logged in to create a school.",
                500: "There was an error processing the form. Please try again later.",
                unknown:
                  "An unknown error occurred. Please refresh and try again later.",
              },
              step1: {
                title: "Before you start creating your school account",
                thingsToKnow:
                  "There are a few things you need to know before you set up an account for your school:",
                employee:
                  "You need to be an <0>employee of the school</0>, however you do not need to be a teacher.",
                owner:
                  "You will become the <0>owner of the school account</0>. This comes with responsibilities you will need to agree to.",
                verification:
                  "Your school will need to be <0>verified by our team</0>. We will contact you within 5 working days.",
                email:
                  "You will <0>not be able to change your email address</0> or delete your Raspberry Pi Foundation account while you are the owner of the school account. You will have to contact us to do this.",
                notSchoolQuestion:
                  "Can I use this if I am not part of a school?",
                notSchoolAnswer:
                  "No, Code Editor for Education is only available to schools at this time.",
              },
              step2: {
                title: "Do you agree to be responsible for the school account?",
                owner:
                  "Creating the school account will make you the <0>owner</0>.",
                responsibilities:
                  "As an account <0>owner</0> you must agree to:",
                responsibility1:
                  "Have the authority to create student accounts within your school account.",
                responsibility2:
                  "Be responsible for how students use the platform within your school account.",
                responsibility3:
                  "Make sure that features are used in line with your school's internal policies and practices.",
                responsibility4:
                  "Be responsible for safeguarding within your school account.",
                termsAndConditions:
                  "Your full responsibilities are outlined in the school account <0>Terms and Conditions</0>, so please ensure you read these.",
                agreeAuthority:
                  "I have the authority to create this account on behalf of my school.",
                agreeTermsAndConditions:
                  "I agree to be responsible for this school account and I accept the <0>Terms and Conditions</0>.",
                agreeResponsibilitySafeguarding:
                  "I agree to be responsible for safeguarding within this school account.",
                subscribeToResearch:
                  "I agree to being contacted about user research opportunities in relation to Code Editor for Education. I can unsubscribe at any time. (optional)",
                validation: {
                  errors: {
                    message:
                      "You must agree to the following responsibilities and Terms and Conditions to continue.",
                    agreeAuthority: "You must confirm you have authority",
                    agreeTermsAndConditions:
                      "You must confirm you accept responsibility",
                    agreeResponsibilitySafeguarding:
                      "You must confirm you accept responsibility for safeguarding within this school account.",
                  },
                },
              },
              step3: {
                title: "What is your role at the School?",
                optionalInfo:
                  "Providing this information is optional, however it helps us to understand our users, how the platform is used, and how we can improve it.",
                role: "What is your role at the School? (Optional)",
                otherRole: "Please tell us your role",
                select: "Please select",
                teacher: "Teacher",
                headOfDepartment: "Head of Department",
                admin: "Administrative staff",
                other: "Other",
                department: "What department are you part of? (Optional)",
                departmentHint:
                  "For example, Computing, IT, Science, English, Art, etc.",
              },
              step4: {
                title: "Tell us about your school",
                schoolDetails:
                  "In order to set up your school in the Editor, we require some details about your school so we can verify it.",
                schoolName: "School name",
                schoolWebsite: "School website",
                schoolAddress: "School address",
                schoolAddress1: "Address line 1",
                schoolAddress2: "Address line 2 (Optional)",
                schoolCity: "Village/Town/City",
                schoolState: "State/County/Province",
                schoolPostcode: "Postal code/Zip code",
                schoolCountry: "Country",
                select: "Please select",
                schoolUrn: "School URN (Optional)",
                schoolUrnHint:
                  "This can be found on the UK Government website <0>here</0>. Although this is not required it will help us to verify your school account.",
                validation: {
                  errors: {
                    message: "Please check the fields.",
                    schoolName: "You must supply your school's name",
                    schoolWebsite: "The school website doesn't look right.",
                    schoolAddress1:
                      "You must supply Address line 1 of your school.",
                    schoolCity:
                      "You must supply your school's Village/Town/City.",
                    schoolState:
                      "You must supply your school's State/County/Province.",
                    schoolPostcode:
                      "You must supply your school's Postal code/Zip code.",
                    schoolCountry: "Please select a country from the list.",
                  },
                },
              },
            },
            error: {
              title: "Sorry, there's been a problem loading the form.",
              copy: "Please try again later.",
              tryAgain: "Try again",
            },
          },
          schoolAlreadyExists: {
            title: "You already have a School account",
            explanation:
              "There's already an account linked to this Raspberry Pi Foundation account. If you wish to create another account for a different school, you will need to use a different Raspberry Pi Foundation account.",
            contact:
              "If you have any issues you can contact us via email: <0>code.editor.registrations@raspberrypi.org</0>.",
            exploreProjects: "Explore our projects",
            editorHome: "Code Editor home",
          },
          schoolBeingVerified: {
            title: "Your school account is being verified",
            text: "You have already set up a school account and it is now in the process of being verified. If you wish to set up another account for a different school, you must use a different Raspberry Pi Foundation account.",
            next: "What happens next?",
            contact:
              "If you have any issues you can contact us via email: <0>code.editor.registrations@raspberrypi.org</0>. Please wait for 5 working days before contacting us about verifying your school.",
            listItems: {
              item1:
                "Thank you for providing all the information needed to set up your school account.",
              item2:
                "We will verify your school. This may take up to 5 working days. You'll receive a confirmation email once it's been verified.",
              item3:
                "Once your school has been verified, you will be able to log in with your Raspberry Pi Foundation account and access your school dashboard, and create a class.",
            },
            exploreProjects: "Explore our projects",
            editorHome: "Code Editor home",
            backToExperienceCs: "Back to Experience CS",
            exploreProjectsExCS:
              "In the meantime you can still explore the projects in the Experience CS curriculum and plan your syllabus.",
          },
          schoolCreated: {
            title: "School account created!",
            text: "Thank you for setting up your school account in the Code Editor!",
            next: "What happens next?",
            contact:
              "If you have any issues you can contact us via email: <0>code.editor.registrations@raspberrypi.org</0>. Please wait for 5 working days before contacting us about verifying your school.",
            listItems: {
              item1:
                "Thank you for providing all the information needed to set up your school account.",
              item2:
                "We will verify your school. We will contact you within 5 working days and you'll receive a confirmation email once it's been verified.",
              item3:
                "Once your school has been verified, you will be able to log in to the Code Editor with your Raspberry Pi Foundation account and access your school dashboard.",
            },
            exploreProjects: "Explore our projects",
            editorHome: "Code Editor home",
          },
          schoolDashboard: {
            copyLink: "Copy link",
            loginCode: "School login code",
            loginCodeExplanation:
              "Students will need to use your school code on the student login page.",
            manageMembers: "Manage members",
            error: {
              title: "Sorry, there's been a problem loading your dashboard.",
              copy: "Please log out, or try again later.",
              tryAgain: "Try again",
            },
          },
          lessonPage: {
            copyLessonModal: {
              cancel: "Cancel",
              copy: "Copy to classes",
              description:
                "Copy this project to reuse the existing project code. Existing student work will not be copied.",
              noClassesSelected: "No classes selected",
              noResults: "No classes found",
              search: "Find a class",
              selectedClasses: "Copy project to:",
              thisClass: "(This class)",
              title: "Create a copy of this project",
            },
            copyLesson: "Copy project",
            editLesson: "Edit project",
            hideLesson: "Hide from students",
            lessonSettings: "Lesson settings",
            showLesson: "Show to students",
            viewCode: "View project code",
          },
          lessons: {
            addProject: "Add project",
            description:
              "Projects are shared with students and contain starter code created by a teacher.",
            noProjects: "No projects",
            title: "Projects",
          },
          validationErrors: {
            nameLength:
              "You must supply a name that's at least 5 characters long",
            usernameLength:
              "You must supply a username that's at least 5 characters long",
            passwordLength:
              "You must supply a password that's at least 8 characters long.",
          },
          memberFilters: {
            owners: "Owners",
            teachers: "Teachers",
            students: "Students",
          },
          membersPage: {
            title: "School members",
            editMember: {
              editDetails: "Edit details",
              changePassword: "Change password",
            },
            inviteTeacher: {
              buttons: {
                cancel: "Cancel",
                createTeacher: "Invite teacher",
              },
              heading: "Invite a teacher",
              teacherPermissions: "Teachers are able to:",
              teacherList: [
                "View and create classes and projects. In future, they will be able to edit and delete these too.",
                "Share projects with students and view their work.",
                "View, create, and edit student accounts. They can also remove students from classes.",
              ],
              ownerPermissions:
                "Owners have the same permission as teachers but can also:",
              ownerList: [
                "Invite and remove teachers.",
                "In future, they will be able to edit the school's details.",
                "In future, they will be able to invite teachers to become owners.",
              ],
              form: {
                label: "Teacher’s email address",
                hint: "This will be the email address that receives the invite and that the teacher will use to log in to the school account.",
                errors: {
                  title: "The invitation couldn't be sent",
                  api: "Sorry, there was a problem submitting the form, please try again later.",
                  presence: "Error: you must provide an email address",
                  invalid: "Error: email address is not valid",
                },
                success: {
                  title: "The teacher has been invited",
                  text: "The teacher will soon receive an email where they can log in to the school account.",
                },
              },
            },
            noMembersFound: "No members found",
            createStudentChoice: {
              title: "Create new students",
              text: "How many students do you want to add?",
              single: "Add a single student",
              multiple: "Add multiple students",
              cancel: "Cancel",
              continue: "Continue",
            },
            createSingleStudent: {
              title: "Add a single student",
              explanation:
                "You will need to provide these details to the student for them to log in.",
              name: "Student's name",
              nameHint:
                "This should be the student's real name. This will only be visible to teachers.",
              nameMissing: "You must supply a name",
              username: "Username",
              usernameHint:
                "This username should be unique within this school account and should NOT contain the student's real name. If your school already has unique identifiers for students, we recommend using these.",
              usernameMissing: "You must supply a username",
              password: "Temporary password",
              passwordHint:
                "Passwords must be at least 8 characters long and difficult to guess (see Help for more information). You will need to share this password with the student. They will choose their own password when they log in for the first time.",
              passwordLength:
                "You must supply a password that's at least 8 characters long.",
              createStudent: "Add student",
            },
            createMultipleStudents: {
              title: "Bulk create student accounts",
              explanation:
                "Create accounts in bulk using <0>this CSV format</0>.",
              rowWarning:
                "Your CSV file should not contain more than {{limit}} rows.",
              csvUploadHint: "<0>Choose a file</0> or drag it here",
              removeUpload: "Remove upload",
              fileUploaded: "File uploaded",
              createStudents: "Add students",
              errorTitle: "Errors found when creating new students",
              errorDescription:
                "Please correct these errors in your CSV file, reupload, and try again.",
              reUpload: "Reupload CSV",
              requestedTitle: "Bulk student creation requested",
              requestedExplanation:
                "Creating students in bulk can take some time depending on the number of students created. You can see the status of this request on the School members page.",
              done: "Done",
              tryAgain: "Try again",
              csvUnexpectedHeaders:
                "Error: Your CSV file has unexpected headers, please check your CSV is following the format provided.",
              csvInvalidRows:
                "Error: Your CSV file has invalid rows, please check your CSV is following the format provided.",
              csvTooManyRows:
                "Error: You have more than {{limit}} rows in your CSV file.",
              jobConcurrencyError: {
                title: "Unable to create students in bulk",
                description:
                  "A request to create multiple students is already in progress, please allow this to complete before making another request. You can see the status of this request on the School members page.",
              },
              standardError: {
                title:
                  "We encountered an issue when trying to create these students",
              },
              addingStudents: "Adding students",
            },
            changeStudentPassword: {
              title: "Change student's password",
              text: "New temporary password",
              password: "New temporary password",
              passwordHint:
                "Passwords must be at least 8 characters long and difficult to guess (see Help for more information). You will need to share this password with the student. They will choose their own password when they next log in.",
              cancel: "Cancel",
              continue: "Continue",
            },
            editStudentDetails: {
              title: "Edit student details",
              studentName: "Student name",
              studentNameExplanation:
                "This should be the student’s real name. This will only be visible to teachers.",
              studentUsername: "Student username",
              studentUsernameExplanation:
                "This username should be unique within this school account and should NOT contain the student’s real name. We recommend using a unique identifier that is already used in your school for this student.",
              cancel: "Cancel",
              save: "Save changes",
              errorName: "This name already exists",
              errorUsername: "This username already exists",
            },
            jobStatus: {
              information: {
                title:
                  "In progress: {{studentCount}} students are currently being created",
                text: "{{studentCount}} students are being created, this will take some time to finish. All the students will be seen here once completed.",
              },
              success: {
                title: "Complete: {{studentCount}} students have been created",
                text: "You can now add these students to classes.",
              },
              error: {
                title: "Failed: Unable to create {{studentCount}} students",
                text: "Your request to create multiple students has failed. If this has happened we are aware of the problem and are actively trying to resolve this!",
              },
            },
          },
          membersPageHeader: {
            title: "School members",
            text: "These are all the members in your school, including students, teachers, and owners. Please get in touch with us at websupport@raspberrypi.org to remove a teacher from your school.",
            invite: "Invite teacher",
            create: "Add student",
          },
          classMembersPage: {
            addMembers: "Add member to class",
            classMembers: "Class members",
            title: "Class members ({{numberOfMembers}})",
            yourSchool: "Your School",
            menu: {
              changePassword: "Change password",
              removeFromClass: "Remove from class",
            },
            addMembersModal: {
              add: "Add selected members",
              cancel: "Cancel",
              description:
                "Adding <0>students</0> to a class will allow them to access all the visible projects within the class.",
              inClass: "In class",
              noResults: "No members found",
              noStudentsSelected: "No school members selected",
              search: "Search for an existing school member to add",
              selectedStudents: "Students that will be added",
              title: "Class members",
            },
            removeStudent: {
              title: "Remove member from class",
              text: "You are about to remove <strong>{{name}}</strong>.",
              panelTitle: "Removing a student",
              panelText:
                "This removes them from the class, but does not delete the student or their work from the school account. Any work they’ve done will remain in the class.",
              cancel: "Cancel",
              remove: "Remove from class",
            },
          },
          classPage: {
            classMembers: "Class members",
            yourSchool: "Your School",
            classSettings: "Class settings",
            delete: "Delete class",
            edit: "Edit class",
            duplicate: "Duplicate class",
          },
          footer: {
            accessibility: "Accessibility",
            charityNameAndNumber:
              "Raspberry Pi Foundation UK registered charity 1129409",
            cookies: "Cookies",
            privacy: "Privacy",
            safeguardingConcern: "Do you have a safeguarding concern?",
            contactUs: "Report a concern",
            safeguarding: "Safeguarding",
            termsAndConditions: "Terms & Conditions",
            help: "Help",
            feedback: "Give Feedback",
          },
          globalNav: {
            accountMenu: {
              login: "Log in",
              logout: "Log out",
              profile: "My profile",
              projects: "My projects",
            },
            accountMenuDefaultAltText: "Account menu",
            accountMenuProfileAltText: "{{name}}'s account",
            raspberryPiLogoAltText: "Raspberry Pi logo",
          },
          projectName: {
            label: "Project name",
            newProject: "New Project",
          },
          header: {
            download: "Download",
            downloadFileNameDefault: "my {{project_type}} project",
            editorLogoAltText: "Editor logo",
            projects: "Your projects",
            renameProject: "Edit project name",
            renameSave: "Save project name",
            save: "Save",
            loginToSave: "Log in to save",
            settings: "Settings",
          },
          imagePanel: {
            gallery: "Image Gallery",
          },
          infoPanel: {
            info: "Information",
          },
          instructionsPanel: {
            nextStep: "Next step",
            previousStep: "Previous step",
            projectSteps: "Project steps",
          },
          projectsPanel: {
            projects: "Projects",
            yourProjectsButton: "Go to your projects",
            projectTypeLabel: "Project type",
          },
          settingsPanel: {
            info: "Settings",
          },
          input: {
            comment: {
              py5: "Py5: imported mode",
            },
          },
          loginToSaveModal: {
            cancel: "Cancel",
            downloadButtonText: "Download",
            downloadText:
              "Or you can download your project and save it on your computer.",
            heading: "Save your project",
            loginButtonText: "Log in to save",
            loginText:
              "Log in to your Raspberry Pi account to save your work, and you'll be able to access and edit your project whenever you need to.",
          },
          mobile: {
            code: "Code",
            menu: "Menu",
            output: "Output",
            preview: "Preview",
            steps: "Steps",
          },
          modals: {
            close: "Close",
          },
          projectModal: {
            cancel: "Cancel",
            createHeading: "Create a new project",
            createProject: "Create project",
            projectName: {
              default: "Untitled",
              helpText: "You can always rename your project later",
              helpTextForLesson:
                "Your project name is visible to your students.",
              inputLabel: "Project name",
              inputLabelForLesson: "Project name",
              missing: "You must supply a project name",
            },
            projectType: {
              html: "HTML",
              inputLabel: "What kind of project do you want to make?",
              inputLabelForLesson:
                "What kind of project do you want to make for your students?",
              helpTextForLesson:
                "This cannot be changed once you have created the project.",
              python: "Python",
              missing: "You must supply a project type",
            },
            updateHeading: "Edit project details",
            updateProject: "Update project",
          },
          notifications: {
            classCreated: "Class created",
            classUpdated: "Class details updated",
            classMemberAdded: "Class member(s) added",
            close: "close",
            lessonCopied: "Project copied to class(es)",
            lessonUpdated: "Project details updated",
            loginPrompt:
              "To save this project and access it later, don't forget to log in or sign up!",
            projectRemixed: "Your remixed project has been saved",
            projectRenamed: "You have renamed your project.",
            projectSaved: "Your project has been saved",
            savePrompt:
              'Save this project to access it later under "Your projects".',
            studentCreated: "Student created",
            studentPasswordChanged: "Student password changed",
            studentDetailsChanged: "Student details changed",
            studentsCreated: "Students created",
            teacherInvited: "Teacher invite sent",
            studentRemovedFromClass: "Student removed from class",
          },
          output: {
            errors: {
              interrupted: "Execution interrupted",
            },
            newTab: "Preview in new tab",
            preview: "preview",
            senseHat: {
              controls: {
                colour: "colour",
                humidity: "humidity",
                motion: "motion",
                motionSensorOptions: {
                  no: "No",
                  yes: "Yes",
                },
                name: "Space Station Control Panel",
                pressure: "pressure",
                temperature: "temperature",
                timer: "timer",
              },
              model: {
                pitch: "pitch",
                roll: "roll",
                yaw: "yaw",
              },
            },
            textOutput: "Text output",
            visualOutput: "Visual output",
          },
          outputViewToggle: {
            buttonTabLabel: "Tabbed view",
            buttonTabTitle: "Tabbed view",
            buttonSplitLabel: "Split view",
            buttonSplitTitle: "Split view",
          },
          project: {
            accessDeniedNoAuthModal: {
              embedded: {
                text: "Visit the Projects site for cool project ideas",
              },
              heading: "You are not able to see this project",
              loginButtonText: "Log in to your account",
              newProject: "Create a new code project",
              projectsSiteLinkText: "Explore Projects site",
              text: "If this is your project, log in to see it. If this is not your project you can visit the Projects site for cool project ideas or to start coding in a new project.",
            },
            accessDeniedWithAuthModal: {
              embedded: {
                text: "Visit the Projects site for cool project ideas",
              },
              heading: "You can't access this project",
              newProject: "Create a new code project",
              projectsSiteLinkText: "Explore Projects site",
              text: "Visit the Projects site for cool project ideas or start coding in a new project.",
            },
            loading: "Loading",
            notFoundModal: {
              embedded: {
                text: "Visit the Projects site for cool project ideas",
              },
              heading: "This project does not exist",
              newProject: "Start new code project",
              projectsSiteLinkText: "Explore Projects site",
              text: "You can start coding in a new project, or visit the Projects site for cool project ideas.",
            },
            untitled: "Untitled project",
          },
          projectHeader: {
            title: "Your projects",
            text: "Select a project to continue coding, view, or edit it.",
          },
          projectList: {
            delete: "Delete",
            deleteLabel: "Delete project",
            deleteProjectModal: {
              cancel: "Cancel",
              delete: "Delete",
              heading: "Delete project",
              text: "Are you sure you want to delete this project?",
            },
            empty: "No projects created yet",
            label: "Open project menu",
            loading: "Loading",
            loadingFailed: "Failed to load projects",
            newProject: "Create a new project",
            pagination: {
              first: "First page",
              last: "Last page",
              next: "Next page",
              previous: "Previous page",
              more: "Load more projects",
            },
            rename: "Rename",
            renameLabel: "Rename project",
            renameProjectModal: {
              cancel: "Do not save",
              heading: "Rename project",
              inputLabel: "Change the name of your project",
              save: "Save",
            },
            updated: "Edited",
            python_type: "Python",
            html_type: "HTML",
          },
          projectPage: {
            back: "Back",
            finished: "Mark as finished",
          },
          projectTypes: {
            html: "HTML/CSS/JS",
            python: "Python",
          },
          runButton: {
            run: "Run",
            stop: "Stop",
            stopping: "Stopping...",
          },
          saveStatus: {
            saving: "Saving",
            saved: "Saved",
          },
          runners: {
            HtmlOutput: "HTML Output Preview",
          },
          secondaryNav: {
            codeEditor: "Code Editor",
            home: "Home",
            education: "For Education",
            projects: "Your Projects",
            school: "Your School",
          },
          sidebar: {
            collapse: "Collapse sidebar",
            download: "Download project",
            expand: "Expand sidebar",
            file: "Project files",
            images: "Image gallery",
            settings: "Settings",
            projects: "Projects",
            information: "Information",
            information_text:
              "Our Code Editor is a tool to help young people learn to code. We have only included functions that are simple and safe to use. That's why, for example, links to other websites are not allowed.",
            instructions: "Project steps",
            feedback: "Feedback",
            privacy: "Privacy",
            cookies: "Cookies",
            accessibility: "Accessibility",
            safeguarding: "Safeguarding",
            charity: "Raspberry Pi Foundation - UK registered charity 1129409",
            settingsMenu: {
              heading: "Settings",
              textSize: "Text size",
              theme: "Theme",
              textSizeOptions: {
                large: "Large",
                medium: "Medium",
                small: "Small",
              },
              themeOptions: {
                dark: "Dark",
                light: "Light",
              },
            },
          },
          studentWork: {
            description:
              "This list contains all the work from students in the class. Only students who have saved their project will appear here. A 'Ready to mark' tag will appear when the student has marked their project as finished.",
            lastEdited: "Last edited: {{time}}",
            noWork: "No student work",
            title: "Student work",
            savingWork: {
              title: "Updating project code and instructions",
              defaultDescription:
                "Any changes you make will not show to students that have already clicked save in this project.<0/><0/>If you need to edit the code or instructions after students have saved, we recommend creating a new project.<0/><0/>If students have not saved this project, you can hide it then make the changes. When students open the project, they’ll start from the last autosaved version of your code and instructions.",
              scratchDescription:
                "When adding projects from Experience CS, teachers are currently unable to edit the <1>project</1> content. These projects are shared as provided in the curriculum, with default instructions and structure.<0/><0/><1>Customisation</1> capabilities for both project content and instructions are coming soon. In the meantime, you can supplement with class notes or external resources as needed.",
            },
          },
          webComponent: {
            loading: "Loading",
          },
          webinarBanner: {
            buttonLabel: "close",
            message:
              "👋 Learn more about Code Editor for Education in an upcoming webinar! ",
            registerNow: "Register now.",
          },
          errors: {
            apiErrorTitle: "There was a problem submitting the form",
            missingOption: "You must select an option to continue.",
          },
          loadingStates: {
            loading: "Loading...",
          },
          listItem: {
            readyToMark: "Ready to mark",
          },
          memberTypes: {
            student: "Student",
            teacher: "Teacher",
            owner: "Owner",
          },
          filters: {
            title: "Filter by:",
            description:
              "Selecting a filter option will automatically update the members list",
          },
          userTypeLogin: {
            title: "Are you a teacher or a student?",
            subtitle:
              "You'll need to log into your school account to view this page",
            teacher: "I'm a teacher",
            student: "I'm a student",
            goEditorHome: "Go to Code Editor home",
          },
          copyLink: {
            copied: "Copied!",
            copy: "Copy link",
          },
          unableToAccess: {
            title: "Unable to access this page",
            codeEditorHome: "Code Editor home",
            goToYourSchool: "Go to your school",
            couldBeBecause: "This could be because:",
            doesNotExist: "The page does not exist",
            incorrectLink: "You have used an incorrect link",
            noAccessToPage: "You do not have access to the page",
          },
          interstitialPage: {
            title: "<0>Experience CS</0> and <0>Code Editor for Education</0>",
            text: {
              intro:
                "Experience CS integrates with the Code Editor for Education — a free, teacher-friendly platform that makes it easy to assign coding projects and manage your classroom.",
              list_preamble: "Here's what you can do:",
              list_item_1:
                "✅ Effortlessly manage assignments and student work",
              list_item_2: "📈 Track progress across your classroom",
              list_item_3: "🔄 Customize and assign Scratch",
              outro:
                "To get started, you'll need to create a free school account. We verify each school to keep the platform secure. Verification can take a few days, so create your school now and be ready for when teaching begins!",
            },
            buttons: {
              learnMore: "Learn more",
              createSchoolAccount: "Create a school account",
            },
            imageAltText: "Experience CS and Code Editor for Education",
          },
          passwordField: {
            show: "Show",
            hide: "Hide",
          },
          errorModal: {
            error: "Error",
          },
          memberRoles: {
            teacher: "Teacher",
            student: "Student",
            owner: "Owner",
          },
          button: {
            yes: "Yes",
            no: "No",
          },
        },
      },
      "xx-XX": {
        translation: {
          modal: {
            close: "Schließen",
            error: {
              heading: "Ein Fehler ist aufgetreten",
              externalLink: {
                message:
                  "Leider sind Links zu externen Seiten im Editor nicht verfügbar.",
              },
            },
          },
          betaBanner: {
            buttonLabel: "schließen",
            buttonTitle: "schließen",
            message: "Der Code-Editor befindet sich in der",
            feedbackText: "Gib uns dein",
            feedback: "Feedback",
            feedbackImprove: ", um uns bei der Verbesserung zu helfen.",
            modal: {
              close: "Schließen",
              heading: "Code-Editor ist in der Beta-Phase",
              meaningHeading: "Was bedeutet Beta?",
              meaningText:
                "Beta bedeutet, dass wir noch nicht ganz fertig sind und einige Dinge möglicherweise nicht so gut aussehen oder funktionieren, wie wir es uns wünschen. Es bedeutet aber auch, dass du einer der Ersten bist, der unseren neuen Code-Editor benutzt!",
            },
            modalLink: "Beta",
            beta: "Beta",
          },
          betaFeedback: {
            title: "Feedback & Updates",
            beta: "Beta",
            text: "Der Code-Editor für Bildung befindet sich derzeit in der Beta-Phase. Füllen Sie diese Umfrage aus, um uns Ihr Feedback mitzuteilen.",
            productUpdates: {
              date: "🚀 Neueste Updates",
              list: [
                "Schüler anzeigen, die ihre Projekte abgeschlossen haben",
                "Zusätzliche Lehrer zu Ihrer Klasse hinzufügen",
              ],
              link: "Vollständige Updates anzeigen",
            },
          },
          classes: {
            teacherTitle: "Klassen",
            teacherDescription:
              "Klassen werden verwendet, um Schüler zu gruppieren und ihnen Projekte zuzuweisen.",
            studentTitle: "Deine Klassen",
            studentDescription:
              "Hier sind alle Klassen, zu denen du hinzugefügt wurdest.",
            createClass: "Klasse erstellen",
            noClasses: "Du hast momentan keine Klassen",
          },
          classMembers: {
            noMembers: "Diese Klasse hat noch keine Mitglieder",
          },
          classModal: {
            create: {
              title: "Eine neue Klasse erstellen",
              confirm: "Klasse erstellen",
              cancel: "Abbrechen",
            },
            update: {
              title: "Klassendetails bearbeiten",
              confirm: "Klassendetails aktualisieren",
              cancel: "Abbrechen",
            },
            description:
              "Klassen werden verwendet, um Schüler zu gruppieren und ihnen Projekte zuzuweisen. Wir empfehlen eine konsistente Namenskonvention, damit Sie Ihre Klassen leicht identifizieren können.",
            className: "Klassenname",
            classNameHint:
              "Dein Klassenname wird für deine Schüler sichtbar sein",
            classDescription: "Klassenbeschreibung (optional)",
            classDescriptionHint:
              "Deine Klassenbeschreibung wird für deine Schüler sichtbar sein",
            nameMissing: "Du musst einen Namen angeben",
          },
          editorHome: {
            text: "Code-Editor Startseite",
          },
          editorPanel: {
            ariaLabel: "Texteingabe des Editors",
          },
          filePanel: {
            errors: {
              containsSpaces: "Dateinamen dürfen keine Leerzeichen enthalten.",
              generalError: "Fehler",
              notUnique: "Dateinamen müssen eindeutig sein.",
              or: "oder",
              unsupportedExtension:
                "Dateinamen müssen mit {{allowedExtensions}} enden.",
            },
            files: "Projektdateien",
            images: "Bildergalerie",
            newFileButton: "Datei hinzufügen",
            newFileModal: {
              cancel: "Abbrechen",
              heading: "Eine neue Datei zu deinem Projekt hinzufügen",
              helpText:
                "Denke daran, die Dateiendung am Ende deines Dateinamens hinzuzufügen, zum Beispiel {{examples}}",
              helpTextExample: {
                html: "'datei.html', 'datei.css' oder 'datei.js'",
                python: "'datei.py'",
              },
              inputLabel: "Benenne deine Datei",
              addFile: "Datei hinzufügen",
            },
            renameFileModal: {
              cancel: "Abbrechen",
              heading: "Datei umbenennen",
              inputLabel: "Benenne deine Datei",
              save: "Speichern",
            },
            fileMenu: {
              label: "Datei-Menü öffnen",
              renameItem: "Datei umbenennen",
            },
          },
          downloadPanel: {
            heading: "Speichern & Herunterladen",
            logInTitle: "Melde dich an, um deinen Fortschritt zu speichern",
            logInHint:
              "Mit einem Raspberry Pi Konto kannst du deinen Code und den Fortschritt deiner Projektschritte speichern.",
            logInButton: "Zum Speichern anmelden",
            signUpButton: "Anmelden",
            downloadHint:
              "Lade deine Projektdateien herunter, damit du sie offline und in einem anderen Code-Editor verwenden kannst.",
            downloadButton: "Projekt herunterladen",
          },
          landingPage: {
            title: "Code-Editor",
            subtitle:
              "Starte mit dem Programmieren, keine Einrichtung erforderlich!",
            chooseLanguage: "Was möchtest du coden?",
            python: "Python",
            html: "HTML/CSS",
            login: "Anmelden",
            whereToStart: "Du weißt nicht, wo du anfangen sollst?",
            projectPython: "Python-Pfad",
            projectHtml: "Web-Pfad",
            loginMessage:
              "Haben Sie ein Konto?<loginButton/>und setzen Sie Ihre Projekte fort",
            followAPath:
              "Folgen Sie einem <pythonLink>{{python}}</pythonLink> oder <htmlLink>{{html}}</htmlLink> auf unserer Projektsite.",
          },
          educationLandingPage: {
            hero: {
              title:
                "Derselbe großartige Code-Editor, jetzt in deinem Klassenzimmer.",
              subtitle: "Jetzt kostenlos, für immer kostenlos.",
              imageAlt:
                "Ein junges Mädchen benutzt den Code-Editor in einem Klassenzimmer.",
              createSchool: "Schulkonto erstellen",
              logIn: "Als Schüler anmelden",
              schoolDashboard: "Deine Schule anzeigen",
            },
            beta: {
              title: "Jetzt in der Beta-Phase!",
              text: "Der Code-Editor für Bildung befindet sich derzeit in der Beta-Phase. Das bedeutet, dass Sie die Plattform früher nutzen können, aber noch nicht alle Funktionen vollständig sind. Wenn Sie mehr erfahren möchten, lesen Sie unsere Hilfeartikel.",
            },
            getStarted: {
              title: "Registriere deine Schule, um loszulegen!",
              button: "Schulkonto erstellen",
            },
            ide: {
              title: "Eine IDE für Lernende entwickelt",
              text: "Unsere integrierte Entwicklungsumgebung (IDE) wurde speziell auf die Bedürfnisse junger Menschen zugeschnitten und hilft, das Erlernen textbasierter Programmierung für Kinder ab 9 Jahren einfach und zugänglich zu machen. Sie ist sicher, altersgerecht und für den Einsatz im Klassenzimmer geeignet.",
              imageAlt:
                "Ein Screenshot des Code-Editors, der ein Python-Projekt zeigt, das geometrische Muster aus einer Zeichenkette generiert. Neben dem Code gibt es einige Anweisungen für den Schüler.",
            },
            free: {
              title: "Alle Funktionen, völlig kostenlos",
              text: "Greife im Webbrowser auf den Code-Editor zu, ohne zusätzliche Software installieren zu müssen. Füge deinem Schulkonto eine unbegrenzte Anzahl von Projekten, Lektionen, Lehrern und Schülern hinzu, alles kostenlos.",
            },
            engage: {
              title: "Erstelle ansprechende Programmierlektionen",
              text: "Erstelle benutzerdefinierte Programmierprojekte, an denen deine Schüler arbeiten können. Hilf ihnen, ihre eigenen Spiele und Kunstwerke mit Python zu programmieren oder Webseiten in HTML/CSS/JavaScript zu gestalten. Deine Schüler können mit unserer großen Auswahl an Python-Bibliotheken kreativ mit Code werden!\n\nDu kannst deine Schüler auch unterstützen und ihnen Hilfestellung geben, indem du den Projekten, die du mit ihnen teilst, benutzerdefinierte Anweisungen hinzufügst.",
              imageAlt:
                "Eine Collage von Bildern, die verschiedene visuelle Ausgaben des Code-Editors zeigen. Zum Beispiel geometrische Muster, eine Sonnensystem-Simulation und eine Kartenvisualisierung.",
            },
            feedback: {
              title: "Gib Schülern personalisiertes Feedback",
              text: "Überprüfe einfach den Fortschritt deiner Schüler, sieh dir ihre Programmierprojekte an und teile jedem Schüler individuelles, sofortiges Feedback mit, um sie auf dem richtigen Weg zu halten.",
              imageAlt:
                "Ein Screenshot aus dem Code-Editor, der den Kommentar eines Lehrers zu einem Schülercode zeigt. Der Kommentar des Lehrers lautet: „Sieht toll aus! Nur ein kleiner Fehler muss behoben werden.“ Er bezieht sich auf einen Tippfehler im Code des Schülers.",
              tag: "Zukünftige Funktion",
            },
            class: {
              title: "Einfache und unkomplizierte Klassenverwaltung",
              text: "Wie den Editor selbst haben wir auch unsere Schnittstelle für Pädagogen nach dem Feedback von Lehrern sauber, einfach und benutzerfreundlich gehalten. Erstelle und verwalte Schülerkonten einfach. Du kannst Schüler massenhaft über eine .csv-Datei hinzufügen, deine Schüler in Klassen organisieren und ihnen helfen, ihre Passwörter schnell zurückzusetzen.",
            },
            safe: {
              title: "Sicher und privat durch Design",
              text: "Konten für Bildung sind so konzipiert, dass sie für Schüler jeden Alters sicher sind. Wir nehmen den Schutz ernst, mit einseitigem Feedback an die Schüler, jederzeitiger Einsicht in ihre Arbeit und verifizierten Schulkonten.\n\nIn Übereinstimmung mit den besten Praktiken zum Schutz von Kindern online minimieren wir unsere Datenerfassung, sodass wir gerade genug haben, um die Sicherheit der Schüler zu gewährleisten.",
            },
            people: {
              title: "Was sagen die Leute über unseren Editor?",
              text: "<i>„Wir haben ihn benutzt und lieben ihn. Die Tatsache, dass er sowohl für HTML / CSS als auch für Python geeignet ist, ist großartig, da die Schüler einen One-Stop-Shop für IDEs haben.“</i><br/><strong>Lee Willis, Leiter für IKT und Informatik, Newcastle High School for Girls</strong><br/><br/><i>„Die Klassenverwaltungs- und Feedback-Funktionen, an denen sie im Moment arbeiten, sehen sehr vielversprechend aus.“</i><br/><strong>Pete Dring, Leiter für Informatik, Fulford School</strong>",
              imageAlt: "Schüler arbeiten an Computern in einem Klassenzimmer.",
            },
          },
          educationLogo: {
            codeEditor: "Code-Editor",
            forEducation: "Für Bildung",
          },
          multiStepForm: {
            cancel: "Abbrechen",
            continue: "Weiter",
            back: "Zurück",
            submit: "Absenden",
            errorTitle: "Es gibt ein Problem mit dem Formular",
            stepText: "Schritt {{current}} von {{total}}",
          },
          invitation: {
            confirmation: {
              title: "Du bist {{schoolName}} beigetreten!",
              button: "Zum Schul-Dashboard",
            },
            invalidToken: {
              title:
                "Du kannst diese Einladung mit diesem Konto nicht annehmen",
              explanationOne:
                "Es sieht so aus, als wärst du eingeladen worden, einer Schule beizutreten, aber du bist mit einem anderen Raspberry Pi Konto angemeldet. Um die Einladung anzunehmen, melde dich bitte mit der E-Mail-Adresse an oder erstelle ein Konto, an die die Einladung gesendet wurde, und versuche dann erneut, den Einladungslink zu öffnen.",
              explanationTwo:
                "Wenn du weiterhin Probleme hast, wende dich bitte an den Schulbesitzer.",
              cancel: "Abbrechen",
            },
            start: {
              subtitle: "Du wurdest eingeladen, beizutreten:",
              continue: "Weiter",
            },
            responsibilities: {
              title: "Akzeptierst du diese Verantwortlichkeiten?",
              listIntro:
                "Als <strong>Lehrer</strong> musst du Folgendem zustimmen:",
              listItemOne:
                "Verantwortlich dafür sein, wie Schüler die Plattform innerhalb deines Schulkontos nutzen.",
              listItemTwo:
                "Sicherstellen, dass Funktionen in Übereinstimmung mit den internen Richtlinien und Praktiken deiner Schule verwendet werden.",
              fullResponsibilities:
                "Deine vollständigen Verantwortlichkeiten sind in den Nutzungsbedingungen des Schulkontos aufgeführt. Bitte lies diese sorgfältig durch.",
              acceptTerms:
                "Ich akzeptiere die Verantwortlichkeiten und die Nutzungsbedingungen.",
              changeEmail:
                "Ich verstehe, dass ich die Raspberry Pi Foundation kontaktieren muss, um meine E-Mail-Adresse zu ändern oder mein Raspberry Pi Foundation-Konto zu löschen, solange ich Teil dieses Schulkontos bin.",
              back: "Zurück",
              joinSchool: "Schule beitreten",
              errorTitle: "Es gibt ein Problem mit dem Formular",
              errorText:
                "Du musst den Verantwortlichkeiten und den Nutzungsbedingungen zustimmen, um fortzufahren.",
            },
          },
          schoolOnboarding: {
            step: "Schritt-Titel",
            steps: {
              errors: {
                401: "Du musst angemeldet sein, um eine Schule zu erstellen.",
                500: "Bei der Verarbeitung des Formulars ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.",
                unknown:
                  "Ein unbekannter Fehler ist aufgetreten. Bitte aktualisiere die Seite und versuche es später noch einmal.",
              },
              step1: {
                title:
                  "Bevor du mit der Erstellung deines Schulkontos beginnst",
                thingsToKnow:
                  "Es gibt ein paar Dinge, die du wissen musst, bevor du ein Konto für deine Schule einrichtest:",
                employee:
                  "Du musst ein Mitarbeiter der Schule sein, du musst jedoch kein Lehrer sein.",
                owner:
                  "Du wirst der Besitzer des Schulkontos werden. Dies bringt Verantwortlichkeiten mit sich, denen du zustimmen musst.",
                verification:
                  "Deine Schule muss von unserem Team verifiziert werden. Wir werden dich innerhalb von 5 Werktagen kontaktieren.",
                email:
                  "Du wirst deine E-Mail-Adresse nicht ändern oder dein Raspberry Pi Foundation-Konto löschen können, solange du der Besitzer des Schulkontos bist. Du musst uns kontaktieren, um dies zu tun.",
                notSchoolQuestion:
                  "Kann ich dies verwenden, wenn ich nicht Teil einer Schule bin?",
                notSchoolAnswer:
                  "Nein, der Code-Editor für Bildung steht derzeit nur Schulen zur Verfügung.",
              },
              step2: {
                title:
                  "Bist du damit einverstanden, für das Schulkonto verantwortlich zu sein?",
                owner:
                  "Durch die Erstellung des Schulkontos wirst du der Besitzer.",
                responsibilities:
                  "Als Besitzer eines Kontos musst du Folgendem zustimmen:",
                responsibility1:
                  "Die Befugnis haben, Schülerkonten innerhalb deines Schulkontos zu erstellen.",
                responsibility2:
                  "Verantwortlich dafür sein, wie Schüler die Plattform innerhalb deines Schulkontos nutzen.",
                responsibility3:
                  "Sicherstellen, dass Funktionen in Übereinstimmung mit den internen Richtlinien und Praktiken deiner Schule verwendet werden.",
                termsAndConditions:
                  "Deine vollständigen Verantwortlichkeiten sind in den Nutzungsbedingungen des Schulkontos aufgeführt. Bitte lies diese sorgfältig durch.",
                agreeAuthority:
                  "Ich habe die Befugnis, dieses Konto im Namen meiner Schule zu erstellen.",
                agreeTermsAndConditions:
                  "Ich bin damit einverstanden, für dieses Schulkonto verantwortlich zu sein, und ich akzeptiere die Nutzungsbedingungen.",
                subscribeToResearch:
                  "Ich bin damit einverstanden, bezüglich Möglichkeiten zur Nutzerforschung im Zusammenhang mit dem Code-Editor für Bildung kontaktiert zu werden. Ich kann mich jederzeit abmelden. (optional)",
                validation: {
                  errors: {
                    message:
                      "Du musst den folgenden Verantwortlichkeiten und Nutzungsbedingungen zustimmen, um fortzufahren.",
                    agreeAuthority:
                      "Du musst bestätigen, dass du die Befugnis hast",
                    agreeTermsAndConditions:
                      "Du musst bestätigen, dass du die Verantwortung übernimmst",
                  },
                },
              },
              step3: {
                title: "Welche Rolle hast du an der Schule?",
                optionalInfo:
                  "Die Angabe dieser Informationen ist optional, hilft uns jedoch, unsere Nutzer, die Nutzung der Plattform und Verbesserungsmöglichkeiten besser zu verstehen.",
                role: "Welche Rolle hast du an der Schule? (Optional)",
                otherRole: "Bitte gib deine Rolle an",
                select: "Bitte auswählen",
                teacher: "Lehrer",
                headOfDepartment: "Fachbereichsleiter",
                admin: "Verwaltungspersonal",
                other: "Andere",
                department: "Zu welcher Abteilung gehörst du? (Optional)",
                departmentHint:
                  "Zum Beispiel Informatik, IT, Naturwissenschaften, Englisch, Kunst usw.",
              },
              step4: {
                title: "Erzähl uns etwas über deine Schule",
                schoolDetails:
                  "Um deine Schule im Editor einzurichten, benötigen wir einige Details, damit wir sie verifizieren können.",
                schoolName: "Name der Schule",
                schoolWebsite: "Webseite der Schule",
                schoolAddress: "Adresse der Schule",
                schoolAddress1: "Adresszeile 1",
                schoolAddress2: "Adresszeile 2 (Optional)",
                schoolCity: "Ort/Stadt",
                schoolState: "Bundesland/Grafschaft/Provinz",
                schoolPostcode: "Postleitzahl",
                schoolCountry: "Land",
                select: "Bitte auswählen",
                schoolUrn: "Schul-URN (Optional)",
                schoolUrnHint:
                  "Diese finden Sie auf der Website der britischen Regierung hier. Obwohl dies nicht erforderlich ist, hilft es uns, Ihr Schulkonto zu verifizieren.",
                validation: {
                  errors: {
                    message: "Bitte überprüfen Sie die Felder.",
                    schoolName: "Sie müssen den Namen Ihrer Schule angeben",
                    schoolWebsite:
                      "Die Schulwebsite scheint nicht korrekt zu sein.",
                    schoolAddress1:
                      "Sie müssen die Adresszeile 1 Ihrer Schule angeben.",
                    schoolCity:
                      "Sie müssen den Ort/die Stadt Ihrer Schule angeben.",
                    schoolState:
                      "Sie müssen das Bundesland/die Grafschaft/Provinz Ihrer Schule angeben.",
                    schoolPostcode:
                      "Sie müssen die Postleitzahl Ihrer Schule angeben.",
                    schoolCountry: "Bitte wählen Sie ein Land aus der Liste.",
                  },
                },
              },
            },
            error: {
              title:
                "Entschuldigung, beim Laden des Formulars ist ein Problem aufgetreten.",
              copy: "Bitte versuchen Sie es später noch einmal.",
              tryAgain: "Erneut versuchen",
            },
          },
          schoolAlreadyExists: {
            title: "Du hast bereits ein Schulkonto",
            explanation:
              "Es ist bereits ein Konto mit diesem Raspberry Pi Foundation-Konto verknüpft. Wenn du ein weiteres Konto für eine andere Schule erstellen möchtest, musst du ein anderes Raspberry Pi Foundation-Konto verwenden.",
            contact:
              "Wenn du Probleme hast, kannst du uns per E-Mail kontaktieren: code.editor.registrations@raspberrypi.org.",
            exploreProjects: "Entdecke unsere Projekte",
            editorHome: "Code-Editor Startseite",
          },
          schoolBeingVerified: {
            title: "Dein Schulkonto wird verifiziert",
            text: "Du hast bereits ein Schulkonto eingerichtet und es wird gerade verifiziert. Wenn du ein weiteres Konto für eine andere Schule einrichten möchtest, musst du ein anderes Raspberry Pi Foundation-Konto verwenden.",
            next: "Was passiert als Nächstes?",
            contact:
              "Wenn du Probleme hast, kannst du uns per E-Mail kontaktieren: code.editor.registrations@raspberrypi.org. Bitte warte 5 Werktage, bevor du uns bezüglich der Verifizierung deiner Schule kontaktierst.",
            listItems: {
              item1:
                "Vielen Dank, dass du alle Informationen zur Einrichtung deines Schulkontos bereitgestellt hast.",
              item2:
                "Wir werden deine Schule verifizieren. Wir werden dich innerhalb von 5 Werktagen kontaktieren und du erhältst eine Bestätigungs-E-Mail, sobald sie verifiziert wurde.",
              item3:
                "Sobald deine Schule verifiziert wurde, kannst du dich mit deinem Raspberry Pi Foundation-Konto im Code-Editor anmelden und auf dein Schul-Dashboard zugreifen.",
            },
            exploreProjects: {
              text: "Entdecke unsere Projekte",
              url: "https://projects.raspberrypi.org",
              plausible: "Entdecke unsere Projekte",
            },
            editorHome: {
              text: "Code-Editor Startseite",
              url: "/",
              plausible: "Code-Editor Startseite",
            },
          },
          schoolCreated: {
            title: "Schulkonto erstellt!",
            text: "Vielen Dank für die Einrichtung deines Schulkontos im Code-Editor!",
            next: "Was passiert als Nächstes?",
            contact:
              "Wenn du Probleme hast, kannst du uns per E-Mail kontaktieren: code.editor.registrations@raspberrypi.org. Bitte warte 5 Werktage, bevor du uns bezüglich der Verifizierung deiner Schule kontaktierst.",
            listItems: {
              item1:
                "Vielen Dank, dass du alle Informationen zur Einrichtung deines Schulkontos bereitgestellt hast.",
              item2:
                "Wir werden deine Schule verifizieren. Wir werden dich innerhalb von 5 Werktagen kontaktieren und du erhältst eine Bestätigungs-E-Mail, sobald sie verifiziert wurde.",
              item3:
                "Sobald deine Schule verifiziert wurde, kannst du dich mit deinem Raspberry Pi Foundation-Konto im Code-Editor anmelden und auf dein Schul-Dashboard zugreifen.",
            },
            exploreProjects: {
              text: "Entdecke unsere Projekte",
              url: "https://projects.raspberrypi.org",
              plausible: "Entdecke unsere Projekte",
            },
            editorHome: {
              text: "Code-Editor Startseite",
              url: "/",
              plausible: "Code-Editor Startseite",
            },
          },
          schoolDashboard: {
            copyLink: "Link kopieren",
            loginCode: "Schul-Login-Code",
            loginCodeExplanation:
              "Schüler müssen Ihren Schulcode auf der Schüler-Login-Seite verwenden.",
            manageMembers: "Mitglieder verwalten",
            error: {
              title:
                "Entschuldigung, beim Laden deines Dashboards ist ein Problem aufgetreten.",
              copy: "Bitte melde dich ab oder versuche es später noch einmal.",
              tryAgain: "Erneut versuchen",
            },
          },
          lessonPage: {
            copyLessonModal: {
              cancel: "Abbrechen",
              copy: "In Klassen kopieren",
              description:
                "Kopiere dieses Projekt, um den vorhandenen Projektcode wiederzuverwenden. Die bisherige Arbeit der Schüler wird nicht kopiert.",
              noClassesSelected: "Keine Klassen ausgewählt",
              noResults: "Keine Klassen gefunden",
              search: "Eine Klasse suchen",
              selectedClasses: "Projekt kopieren nach:",
              thisClass: "(Diese Klasse)",
              title: "Eine Kopie dieses Projekts erstellen",
            },
            copyLesson: "Projekt kopieren",
            editLesson: "Projekt bearbeiten",
            hideLesson: "Vor Schülern verbergen",
            lessonSettings: "Lektionseinstellungen",
            showLesson: "Schülern anzeigen",
            viewCode: "Projektcode anzeigen",
          },
          lessons: {
            addProject: "Projekt hinzufügen",
            description:
              "Projekte werden mit Schülern geteilt und enthalten von einem Lehrer erstellten Startcode.",
            noProjects: "Keine Projekte",
            title: "Projekte",
          },
          validationErrors: {
            nameLength:
              "Du musst einen Namen angeben, der mindestens 5 Zeichen lang ist",
            usernameLength:
              "Du musst einen Benutzernamen angeben, der mindestens 5 Zeichen lang ist",
            passwordLength:
              "Du musst ein Passwort angeben, das mindestens 8 Zeichen lang ist.",
          },
          memberFilters: {
            owners: "Besitzer",
            teachers: "Lehrer",
            students: "Schüler",
          },
          membersPage: {
            title: "Schulmitglieder",
            editMember: {
              editDetails: "Details bearbeiten",
              changePassword: "Passwort ändern",
            },
            inviteTeacher: {
              buttons: {
                cancel: "Abbrechen",
                createTeacher: "Lehrer einladen",
              },
              heading: "Einen Lehrer einladen",
              teacherPermissions: "Lehrer können:",
              teacherList: [
                "Klassen und Projekte anzeigen und erstellen. Zukünftig können sie diese auch bearbeiten und löschen.",
                "Projekte mit Schülern teilen und deren Arbeit anzeigen.",
                "Schülerkonten anzeigen, erstellen und bearbeiten. Sie können Schüler auch aus Klassen entfernen.",
              ],
              ownerPermissions:
                "Besitzer haben die gleichen Berechtigungen wie Lehrer, können aber auch:",
              ownerList: [
                "Lehrer einladen und entfernen.",
                "Zukünftig können sie die Details der Schule bearbeiten.",
                "Zukünftig können sie Lehrer einladen, Besitzer zu werden.",
              ],
              form: {
                label: "E-Mail-Adresse des Lehrers",
                hint: "Dies ist die E-Mail-Adresse, an die die Einladung gesendet wird und die der Lehrer zum Anmelden am Schulkonto verwendet.",
                errors: {
                  title: "Die Einladung konnte nicht gesendet werden",
                  api: "Entschuldigung, beim Übermitteln des Formulars ist ein Problem aufgetreten. Bitte versuchen Sie es später noch einmal.",
                  presence: "Fehler: Sie müssen eine E-Mail-Adresse angeben",
                  invalid: "Fehler: E-Mail-Adresse ist ungültig",
                },
                success: {
                  title: "Der Lehrer wurde eingeladen",
                  text: "Der Lehrer erhält in Kürze eine E-Mail, über die er sich am Schulkonto anmelden kann.",
                },
              },
            },
            noMembersFound: "Keine Mitglieder gefunden",
            createStudentChoice: {
              title: "Neue Schüler erstellen",
              text: "Wie viele Schüler möchtest du hinzufügen?",
              single: "Einen einzelnen Schüler hinzufügen",
              multiple: "Mehrere Schüler hinzufügen",
              cancel: "Abbrechen",
              continue: "Weiter",
            },
            createSingleStudent: {
              title: "Einen einzelnen Schüler hinzufügen",
              explanation:
                "Du musst dem Schüler diese Details für die Anmeldung mitteilen.",
              name: "Name des Schülers",
              nameHint:
                "Dies sollte der richtige Name des Schülers sein. Dieser ist nur für Lehrer sichtbar.",
              nameMissing: "Du musst einen Namen angeben",
              username: "Benutzername",
              usernameHint:
                "Dieser Benutzername sollte innerhalb dieses Schulkontos eindeutig sein und NICHT den echten Namen des Schülers enthalten. Wenn deine Schule bereits eindeutige Kennungen für Schüler hat, empfehlen wir die Verwendung dieser.",
              usernameMissing: "Du musst einen Benutzernamen angeben",
              password: "Temporäres Passwort",
              passwordHint:
                "Passwörter müssen mindestens 8 Zeichen lang und schwer zu erraten sein (siehe Hilfe für weitere Informationen). Du musst dieses Passwort mit dem Schüler teilen. Er wird bei seiner ersten Anmeldung sein eigenes Passwort wählen.",
              passwordLength:
                "Du musst ein Passwort angeben, das mindestens 8 Zeichen lang ist.",
              createStudent: "Schüler hinzufügen",
            },
            createMultipleStudents: {
              title: "Massenweise Schülerkonten erstellen",
              explanation: "Erstelle Konten massenhaft im CSV-Format.",
              rowWarning:
                "Deine CSV-Datei sollte nicht mehr als {{limit}} Zeilen enthalten.",
              csvUploadHint: "Datei auswählen oder hierher ziehen",
              removeUpload: "Hochladen entfernen",
              fileUploaded: "Datei hochgeladen",
              createStudents: "Schüler hinzufügen",
              errorTitle: "Fehler beim Erstellen neuer Schüler gefunden",
              errorDescription:
                "Bitte korrigiere diese Fehler in deiner CSV-Datei, lade sie erneut hoch und versuche es noch einmal.",
              reUpload: "CSV erneut hochladen",
              requestedTitle: "Massenhafte Schülererstellung angefordert",
              requestedExplanation:
                "Das massenhafte Erstellen von Schülern kann je nach Anzahl der erstellten Schüler einige Zeit dauern. Den Status dieser Anfrage kannst du auf der Seite Schulmitglieder einsehen.",
              done: "Fertig",
              tryAgain: "Erneut versuchen",
              csvUnexpectedHeaders:
                "Fehler: Deine CSV-Datei enthält unerwartete Überschriften. Bitte überprüfe, ob deine CSV-Datei dem bereitgestellten Format entspricht.",
              csvInvalidRows:
                "Fehler: Deine CSV-Datei enthält ungültige Zeilen. Bitte überprüfe, ob deine CSV-Datei dem bereitgestellten Format entspricht.",
              csvTooManyRows:
                "Fehler: Deine CSV-Datei enthält mehr als {{limit}} Zeilen.",
              jobConcurrencyError: {
                title: "Massenhafte Schülererstellung nicht möglich",
                description:
                  "Eine Anfrage zur massenhaften Erstellung von Schülern ist bereits in Bearbeitung. Bitte warte, bis diese abgeschlossen ist, bevor du eine weitere Anfrage stellst. Den Status dieser Anfrage kannst du auf der Seite Schulmitglieder einsehen.",
              },
              standardError: {
                title:
                  "Beim Versuch, diese Schüler zu erstellen, ist ein Problem aufgetreten",
              },
              addingStudents: "Schüler werden hinzugefügt",
            },
            changeStudentPassword: {
              title: "Passwort des Schülers ändern",
              text: "Neues temporäres Passwort",
              password: "Neues temporäres Passwort",
              passwordHint:
                "Passwörter müssen mindestens 8 Zeichen lang und schwer zu erraten sein (siehe Hilfe für weitere Informationen). Du musst dieses Passwort mit dem Schüler teilen. Er wird bei seiner nächsten Anmeldung sein eigenes Passwort wählen.",
              cancel: "Abbrechen",
              continue: "Weiter",
            },
            editStudentDetails: {
              title: "Schülerdetails bearbeiten",
              studentName: "Name des Schülers",
              studentNameExplanation:
                "Dies sollte der richtige Name des Schülers sein. Dieser ist nur für Lehrer sichtbar.",
              studentUsername: "Benutzername des Schülers",
              studentUsernameExplanation:
                "Dieser Benutzername sollte innerhalb dieses Schulkontos eindeutig sein und NICHT den echten Namen des Schülers enthalten. Wir empfehlen die Verwendung einer eindeutigen Kennung, die in deiner Schule bereits für diesen Schüler verwendet wird.",
              cancel: "Abbrechen",
              save: "Änderungen speichern",
              errorName: "Dieser Name existiert bereits",
              errorUsername: "Dieser Benutzername existiert bereits",
            },
            jobStatus: {
              information: {
                title:
                  "In Bearbeitung: {{studentCount}} Schüler werden gerade erstellt",
                text: "{{studentCount}} Schüler werden erstellt. Dies wird einige Zeit in Anspruch nehmen. Alle Schüler werden nach Abschluss hier angezeigt.",
              },
              success: {
                title:
                  "Abgeschlossen: {{studentCount}} Schüler wurden erstellt",
                text: "Du kannst diese Schüler jetzt zu Klassen hinzufügen.",
              },
              error: {
                title:
                  "Fehlgeschlagen: {{studentCount}} Schüler konnten nicht erstellt werden",
                text: "Deine Anfrage zur massenhaften Erstellung von Schülern ist fehlgeschlagen. Wenn dies passiert ist, sind wir uns des Problems bewusst und arbeiten aktiv an einer Lösung!",
              },
            },
          },
          membersPageHeader: {
            title: "Schulmitglieder",
            text: "Dies sind alle Mitglieder deiner Schule, einschließlich Schüler, Lehrer und Besitzer. Bitte kontaktiere uns unter websupport@raspberrypi.org, um einen Lehrer von deiner Schule zu entfernen.",
            invite: "Lehrer einladen",
            create: "Schüler hinzufügen",
          },
          classMembersPage: {
            addMembers: "Mitglied zur Klasse hinzufügen",
            classMembers: "Klassenmitglieder",
            title: "Klassenmitglieder ({{numberOfMembers}})",
            yourSchool: "Deine Schule",
            menu: {
              changePassword: "Passwort ändern",
              removeFromClass: "Aus der Klasse entfernen",
            },

            addMembersModal: {
              add: "Ausgewählte Mitglieder hinzufügen",
              cancel: "Abbrechen",
              description:
                "Das Hinzufügen von Schülern zu einer Klasse ermöglicht ihnen den Zugriff auf alle sichtbaren Projekte innerhalb der Klasse.",
              inClass: "In der Klasse",
              noResults: "Keine Mitglieder gefunden",
              noStudentsSelected: "Keine Schulmitglieder ausgewählt",
              search:
                "Suche nach einem vorhandenen Schulmitglied zum Hinzufügen",
              selectedStudents: "Schüler, die hinzugefügt werden",
              title: "Klassenmitglieder",
            },
            removeStudent: {
              title: "Mitglied aus der Klasse entfernen",
              text: "Du bist dabei, <strong>{{name}}</strong> zu entfernen.",
              panelTitle: "Einen Schüler entfernen",
              panelText:
                "Dadurch wird er aus der Klasse entfernt, aber der Schüler oder seine Arbeit nicht vom Schulkonto gelöscht. Alle seine bisherigen Arbeiten bleiben in der Klasse erhalten.",
              cancel: "Abbrechen",
              remove: "Aus der Klasse entfernen",
            },
          },
          classPage: {
            classMembers: "Klassenmitglieder",
            yourSchool: "Deine Schule",
            classSettings: "Klasseneinstellungen",
            delete: "Klasse löschen",
            edit: "Klasse bearbeiten",
            duplicate: "Klasse duplizieren",
          },
          footer: {
            accessibility: "Barrierefreiheit",
            charityNameAndNumber:
              "Raspberry Pi Foundation UK eingetragene Wohltätigkeitsorganisation 1129409",
            cookies: "Cookies",
            privacy: "Datenschutz",
            safeguardingConcern:
              "Hast du Bedenken bezüglich des Kinderschutzes?",
            contactUs: "Ein Problem melden",
            safeguarding: "Kinderschutz",
            termsAndConditions: "Nutzungsbedingungen",
            help: "Hilfe",
            feedback: "Feedback geben",
          },
          globalNav: {
            accountMenu: {
              login: "Anmelden",
              logout: "Abmelden",
              profile: "Mein Profil",
              projects: "Meine Projekte",
            },
            accountMenuDefaultAltText: "Kontomenü",
            accountMenuProfileAltText: "Das Konto von {{name}}",
            raspberryPiLogoAltText: "Raspberry Pi Logo",
          },
          projectName: {
            label: "Projektname",
            newProject: "Neues Projekt",
          },
          header: {
            download: "Herunterladen",
            downloadFileNameDefault: "mein {{project_type}} Projekt",
            editorLogoAltText: "Editor-Logo",
            projects: "Deine Projekte",
            renameProject: "Projektnamen bearbeiten",
            renameSave: "Projektnamen speichern",
            save: "Speichern",
            loginToSave: "Zum Speichern anmelden",
            settings: "Einstellungen",
          },
          imagePanel: {
            gallery: "Bildergalerie",
          },
          infoPanel: {
            info: "Informationen",
          },
          instructionsPanel: {
            nextStep: "Nächster Schritt",
            previousStep: "Vorheriger Schritt",
            projectSteps: "Projektschritte",
          },
          projectsPanel: {
            projects: "Projekte",
            yourProjectsButton: "Zu deinen Projekten gehen",
            projectTypeLabel: "Projekttyp",
          },
          settingsPanel: {
            info: "Einstellungen",
          },
          input: {
            comment: {
              py5: "Py5: importierter Modus",
            },
          },
          loginToSaveModal: {
            cancel: "Abbrechen",
            downloadButtonText: "Herunterladen",
            downloadText:
              "Oder du kannst dein Projekt herunterladen und auf deinem Computer speichern.",
            heading: "Dein Projekt speichern",
            loginButtonText: "Zum Speichern anmelden",
            loginText:
              "Melde dich bei deinem Raspberry Pi Konto an, um deine Arbeit zu speichern. Du kannst dann jederzeit auf dein Projekt zugreifen und es bearbeiten.",
          },
          mobile: {
            code: "Code",
            menu: "Menü",
            output: "Ausgabe",
            preview: "Vorschau",
            steps: "Schritte",
          },
          modals: {
            close: "Schließen",
          },
          projectModal: {
            cancel: "Abbrechen",
            createHeading: "Ein neues Projekt erstellen",
            createProject: "Projekt erstellen",
            projectName: {
              default: "Unbenannt",
              helpText: "Du kannst dein Projekt später jederzeit umbenennen",
              helpTextForLesson:
                "Dein Projektname ist für deine Schüler sichtbar.",
              inputLabel: "Projektname",
              inputLabelForLesson: "Projektname",
              missing: "Du musst einen Projektnamen angeben",
            },
            projectType: {
              html: "HTML",
              inputLabel: "Welche Art von Projekt möchtest du erstellen?",
              inputLabelForLesson:
                "Welche Art von Projekt möchtest du für deine Schüler erstellen?",
              helpTextForLesson:
                "Dies kann nach der Erstellung des Projekts nicht mehr geändert werden.",
              python: "Python",
              missing: "Du musst einen Projekttyp angeben",
            },
            updateHeading: "Projektdetails bearbeiten",
            updateProject: "Projekt aktualisieren",
          },
          notifications: {
            classCreated: "Klasse erstellt",
            classUpdated: "Klassendetails aktualisiert",
            classMemberAdded: "Klassenmitglied(er) hinzugefügt",
            close: "schließen",
            lessonCopied: "Projekt in Klasse(n) kopiert",
            lessonUpdated: "Projektdetails aktualisiert",
            loginPrompt:
              "Um dieses Projekt zu speichern und später darauf zuzugreifen, vergiss nicht, dich anzumelden oder zu registrieren!",
            projectRemixed: "Dein remixtes Projekt wurde gespeichert",
            projectRenamed: "Du hast dein Projekt umbenannt.",
            projectSaved: "Dein Projekt wurde gespeichert",
            savePrompt:
              "Speichere dieses Projekt, um später unter „Deine Projekte“ darauf zuzugreifen.",
            studentCreated: "Schüler erstellt",
            studentPasswordChanged: "Schülerpasswort geändert",
            studentDetailsChanged: "Schülerdetails geändert",
            studentsCreated: "Schüler erstellt",
            teacherInvited: "Lehrereinladung gesendet",
            studentRemovedFromClass: "Schüler aus der Klasse entfernt",
          },
          output: {
            errors: {
              interrupted: "Ausführung unterbrochen",
            },
            newTab: "Vorschau in neuem Tab",
            preview: "Vorschau",
            senseHat: {
              controls: {
                colour: "Farbe",
                humidity: "Luftfeuchtigkeit",
                motion: "Bewegung",
                motionSensorOptions: {
                  no: "Nein",
                  yes: "Ja",
                },
                name: "Steuerungspanel der Raumstation",
                pressure: "Druck",
                temperature: "Temperatur",
                timer: "Timer",
              },
              model: {
                pitch: "Neigung",
                roll: "Rollen",
                yaw: "Gieren",
              },
            },
            textOutput: "Textausgabe",
            visualOutput: "Visuelle Ausgabe",
          },
          outputViewToggle: {
            buttonTabLabel: "Tab-Ansicht",
            buttonTabTitle: "Tab-Ansicht",
            buttonSplitLabel: "Geteilte Ansicht",
            buttonSplitTitle: "Geteilte Ansicht",
          },
          project: {
            accessDeniedNoAuthModal: {
              embedded: {
                text: "Besuche die Projektseite für coole Projektideen",
              },
              heading: "Du kannst dieses Projekt nicht sehen",
              loginButtonText: "Melde dich bei deinem Konto an",
              newProject: "Ein neues Code-Projekt erstellen",
              projectsSiteLinkText: "Projektseite erkunden",
              text: "Wenn dies dein Projekt ist, melde dich an, um es zu sehen. Wenn dies nicht dein Projekt ist, kannst du die Projektseite für coole Projektideen besuchen oder in einem neuen Projekt mit dem Programmieren beginnen.",
            },
            accessDeniedWithAuthModal: {
              embedded: {
                text: "Besuche die Projektseite für coole Projektideen",
              },
              heading: "Du kannst auf dieses Projekt nicht zugreifen",
              newProject: "Ein neues Code-Projekt erstellen",
              projectsSiteLinkText: "Projektseite erkunden",
              text: "Besuche die Projektseite für coole Projektideen oder beginne mit dem Programmieren in einem neuen Projekt.",
            },
            loading: "Wird geladen",
            notFoundModal: {
              embedded: {
                text: "Besuche die Projektseite für coole Projektideen",
              },
              heading: "Dieses Projekt existiert nicht",
              newProject: "Neues Code-Projekt starten",
              projectsSiteLinkText: "Projektseite erkunden",
              text: "Du kannst mit dem Programmieren in einem neuen Projekt beginnen oder die Projektseite für coole Projektideen besuchen.",
            },
            untitled: "Unbenanntes Projekt",
          },
          projectHeader: {
            title: "Deine Projekte",
            text: "Wähle ein Projekt aus, um mit dem Programmieren fortzufahren, es anzuzeigen oder zu bearbeiten.",
          },
          projectList: {
            delete: "Löschen",
            deleteLabel: "Projekt löschen",
            deleteProjectModal: {
              cancel: "Abbrechen",
              delete: "Löschen",
              heading: "Projekt löschen",
              text: "Bist du sicher, dass du dieses Projekt löschen möchtest?",
            },
            empty: "Noch keine Projekte erstellt",
            label: "Projektmenü öffnen",
            loading: "Wird geladen",
            loadingFailed: "Das Laden der Projekte ist fehlgeschlagen",
            newProject: "Ein neues Projekt erstellen",
            pagination: {
              first: "Erste Seite",
              last: "Letzte Seite",
              next: "Nächste Seite",
              previous: "Vorherige Seite",
              more: "Weitere Projekte laden",
            },
            rename: "Umbenennen",
            renameLabel: "Projekt umbenennen",
            renameProjectModal: {
              cancel: "Nicht speichern",
              heading: "Projekt umbenennen",
              inputLabel: "Ändere den Namen deines Projekts",
              save: "Speichern",
            },
            updated: "Bearbeitet",
            python_type: "Python",
            html_type: "HTML",
          },
          projectPage: {
            back: "Zurück",
            finished: "Als fertig markieren",
          },
          projectTypes: {
            html: "HTML/CSS/JS",
            python: "Python",
          },
          runButton: {
            run: "Ausführen",
            stop: "Stoppen",
            stopping: "Wird gestoppt...",
          },
          saveStatus: {
            saving: "Wird gespeichert",
            saved: "Gespeichert",
          },
          runners: {
            HtmlOutput: "HTML-Ausgabe-Vorschau",
          },
          secondaryNav: {
            codeEditor: "Code-Editor",
            home: "Startseite",
            education: "Für Bildung",
            projects: "Deine Projekte",
            school: "Deine Schule",
          },
          sidebar: {
            collapse: "Seitenleiste einklappen",
            download: "Projekt herunterladen",
            expand: "Seitenleiste ausklappen",
            file: "Projektdateien",
            images: "Bildergalerie",
            settings: "Einstellungen",
            projects: "Projekte",
            information: "Informationen",
            information_text:
              "Unser Code-Editor ist ein Werkzeug, um jungen Menschen das Programmieren beizubringen. Wir haben nur Funktionen integriert, die einfach und sicher zu verwenden sind. Deshalb sind beispielsweise Links zu anderen Websites nicht erlaubt.",
            instructions: "Projektschritte",
            feedback: "Feedback",
            privacy: "Datenschutz",
            cookies: "Cookies",
            accessibility: "Barrierefreiheit",
            safeguarding: "Kinderschutz",
            charity:
              "Raspberry Pi Foundation - UK eingetragene Wohltätigkeitsorganisation 1129409",
            settingsMenu: {
              heading: "Einstellungen",
              textSize: "Textgröße",
              theme: "Thema",
              textSizeOptions: {
                large: "Groß",
                medium: "Mittel",
                small: "Klein",
              },
              themeOptions: {
                dark: "Dunkel",
                light: "Hell",
              },
            },
          },
          studentWork: {
            description:
              "Diese Liste enthält alle Arbeiten der Schüler in der Klasse. Nur Schüler, die ihr Projekt gespeichert haben, werden hier angezeigt. Ein Tag „Bereit zur Bewertung“ erscheint, wenn der Schüler sein Projekt als abgeschlossen markiert hat.",
            lastEdited: "Zuletzt bearbeitet: {{time}}",
            noWork: "Keine Schülerarbeiten",
            title: "Schülerarbeiten",
            savingWork: {
              title: "Projektcode und Anweisungen werden aktualisiert",
              description:
                "Änderungen, die du vornimmst, werden Schülern, die in diesem Projekt bereits auf „Speichern“ geklickt haben, nicht angezeigt.<0/><0/>Wenn du den Code oder die Anweisungen bearbeiten musst, nachdem Schüler gespeichert haben, empfehlen wir, ein neues Projekt zu erstellen.<0/><0/>Wenn Schüler dieses Projekt noch nicht gespeichert haben, kannst du es ausblenden und dann die Änderungen vornehmen. Wenn Schüler das Projekt öffnen, beginnen sie mit der zuletzt automatisch gespeicherten Version deines Codes und deiner Anweisungen.",
            },
          },
          webComponent: {
            loading: "Wird geladen",
          },
          webinarBanner: {
            buttonLabel: "schließen",
            message:
              "👋 Erfahre mehr über den Code-Editor für Bildung in einem bevorstehenden Webinar! ",
            registerNow: "Jetzt registrieren.",
          },
          errors: {
            apiErrorTitle:
              "Beim Übermitteln des Formulars ist ein Problem aufgetreten",
            missingOption: "Du musst eine Option auswählen, um fortzufahren.",
          },
          loadingStates: {
            loading: "Wird geladen...",
          },
          listItem: {
            readyToMark: "Bereit zur Bewertung",
          },
          memberTypes: {
            student: "Schüler",
            teacher: "Lehrer",
            owner: "Besitzer",
          },
          filters: {
            title: "Filtern nach:",
            description:
              "Durch Auswahl einer Filteroption wird die Mitgliederliste automatisch aktualisiert",
          },
          userTypeLogin: {
            title: "Bist du ein Lehrer oder ein Schüler?",
            subtitle:
              "Du musst dich bei deinem Schulkonto anmelden, um diese Seite anzuzeigen",
            teacher: "Ich bin ein Lehrer",
            student: "Ich bin ein Schüler",
            goEditorHome: "Zur Code-Editor Startseite",
          },
          copyLink: {
            copied: "Kopiert!",
            copy: "Link kopieren",
          },
          unableToAccess: {
            title: "Zugriff auf diese Seite nicht möglich",
            codeEditorHome: "Code-Editor Startseite",
            goToYourSchool: "Zu deiner Schule gehen",
            couldBeBecause: "Dies könnte daran liegen:",
            doesNotExist: "Die Seite existiert nicht",
            incorrectLink: "Du hast einen falschen Link verwendet",
            noAccessToPage: "Du hast keinen Zugriff auf die Seite",
          },
          passwordField: {
            show: "Zeigen",
            hide: "Bart",
          },
          errorModal: {
            error: "Fehler",
          },
          memberRoles: {
            teacher: "Lehrer",
            student: "Student",
            owner: "Eigentümer",
          },
          button: {
            yes: "Ja",
            no: "Nein",
          },
        },
      },
    },
  });

export default i18n;
